/** @jsxImportSource @emotion/react */
import { PopoverBase } from "./PopoverBase";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { AnchorProps } from "@headlessui/react/dist/internal/floating";
import { useEffect, useState } from "react";
import { TextField } from "./forms/fields/TextField";
import { ButtonActionForwardedRef, ButtonPrimary } from "./NewButtons";
import { css } from "@emotion/react";
import { Divider } from "./Divider";
import { colors } from "./colors";
import { CheckListField } from "./forms/fields/CheckListField";
import {
  CloseButton,
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
} from "@headlessui/react";
import { SelectableItem } from "./SelectableItem";
import { breakpoints } from "./breakpoints";
import { StyledText } from "../assets/typography/typography";
import { sanitizeString } from "../util";
import { EmptyState } from "./EmptyState";

type Props = {
  panelAnchor?: AnchorProps;
  onCreateFilter: (
    filters: {
      name: string;
      choices: string[];
    }[]
  ) => void;
  filterOptions: {
    label?: string;
    name: string;
    icon: React.ReactNode;
    options: {
      value: string;
      label?: string;
    }[];
    singleSelect?: boolean;
  }[];
};

const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  height: 400px;
  max-height: 400px;
  width: 600px;
  ${breakpoints.tablet} {
    width: 300px;
  }
  overflow: hidden;
`;

const topFilterStyle = css`
  display: flex;
  flex-direction: row;
  ${breakpoints.tablet} {
    flex-direction: column;
  }
  align-items: center;
  gap: 8px;
  padding: 8px;
`;

const filterListsWrapperStyle = css`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
`;

const filterListStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  height: 345px;
  overflow-y: scroll;
  padding: 8px;
`;

const leftFilterListStyle = css`
  ${filterListStyle}
  border-right: 1px solid ${colors.greyBorder};
`;

const rightFilterListStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 2px;
`;

export const FilterPopover = ({
  panelAnchor,
  onCreateFilter,
  filterOptions,
}: Props) => {
  const [searchValue, setSearchValue] = useState("");

  const [filters, setFilters] = useState<
    {
      name: string;
      choices: string[];
    }[]
  >(filterOptions.map((option) => ({ name: option.name, choices: [] })));

  useEffect(() => {
    setFilters(
      filterOptions.map((option) => ({ name: option.name, choices: [] }))
    );
  }, [filterOptions, setFilters]);

  return (
    <PopoverBase
      anchor={panelAnchor}
      buttonComponent={
        <ButtonPrimary
          buttonSize="large"
          icon={<FontAwesomeIcon icon={solid("plus")} />}
        >
          Add filter
        </ButtonPrimary>
      }
    >
      <div css={wrapperStyle}>
        <div css={topFilterStyle}>
          <TextField
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search..."
          />
          <CloseButton
            as={ButtonActionForwardedRef}
            type="button"
            onClick={() => {
              onCreateFilter(filters);
              setFilters(
                filterOptions.map((option) => ({
                  name: option.name,
                  choices: [],
                }))
              );
              setSearchValue("");
            }}
          >
            Create filter
          </CloseButton>
        </div>
        <Divider />
        <TabGroup as="div" css={filterListsWrapperStyle}>
          <TabList as="div" css={leftFilterListStyle}>
            {filterOptions.length > 0 ? (
              <>
                {filterOptions.map((option, i) => {
                  const noOfFilters = filters.find(
                    (f) => f.name === option.name
                  )?.choices.length;
                  return (
                    <Tab as="div" key={i}>
                      {({ selected }) => (
                        <SelectableItem selected={selected} icon={option.icon}>
                          {option.label}{" "}
                          {noOfFilters && noOfFilters > 0
                            ? `(${noOfFilters})`
                            : ""}
                        </SelectableItem>
                      )}
                    </Tab>
                  );
                })}
              </>
            ) : (
              <EmptyState
                title="No new filter options"
                subtitle="You have created all the filters available."
              />
            )}
          </TabList>
          <TabPanels as="div" css={filterListStyle}>
            <StyledText
              className="bodySubtextWide mb-0"
              color={colors.textSecondary}
            >
              Options
            </StyledText>
            {filterOptions.length > 0 ? (
              <>
                {filterOptions.map((filterOption, i) => {
                  const filteredOptions = filterOption.options.filter((o) =>
                    sanitizeString(o.label || "").includes(
                      sanitizeString(searchValue)
                    )
                  );
                  return (
                    <TabPanel as="div" key={i} css={rightFilterListStyle}>
                      {filteredOptions.length > 0 ? (
                        <>
                          {filteredOptions.map((option, i) => (
                            <CheckListField
                              key={i}
                              label={option.label}
                              value={option.value}
                              readOnly
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFilters((prev) => {
                                    const newFilters = prev.map((f) => ({
                                      ...f,
                                      choices:
                                        f.name === filterOption.name
                                          ? [
                                              option.value,
                                              ...(filterOption.singleSelect
                                                ? []
                                                : f.choices),
                                            ]
                                          : f.choices,
                                    }));
                                    return newFilters;
                                  });
                                  return;
                                }
                                setFilters((prev) => {
                                  const newFilters = prev.map((f) => ({
                                    ...f,
                                    choices:
                                      f.name === filterOption.name
                                        ? filterOption.singleSelect
                                          ? []
                                          : f.choices.filter(
                                              (c) => c !== option.value
                                            )
                                        : f.choices,
                                  }));
                                  return newFilters;
                                });
                              }}
                              checked={filters
                                .find((f) => f.name === filterOption.name)
                                ?.choices.includes(option.value)}
                            />
                          ))}
                        </>
                      ) : (
                        <EmptyState
                          title="No options found"
                          subtitle="Try searching for a different option."
                        />
                      )}
                    </TabPanel>
                  );
                })}
              </>
            ) : (
              <EmptyState
                title="No filter options"
                subtitle="You have created all the filters available."
              />
            )}
          </TabPanels>
        </TabGroup>
      </div>
    </PopoverBase>
  );
};
